import { z } from "zod";
import {
  COMPARISON_CREATED_SOURCES,
  MEASUREMENTS_LOG_SOURCES,
  MEASUREMENTS_TAB_VIEW_SOURCES,
  NUTRITION_LOG_SOURCES,
  NUTRITION_TELEMETRY_MEAL_TYPES,
  PHOTO_UPLOAD_SOURCES,
  VIDEO_UPLOAD_SOURCES,
  WEIGHT_LOG_SOURCES,
} from "@/lib/analytics/feature-events";

export const TOP_LEVEL_TABS = [
  "home",
  "train",
  "track",
  "nutrition",
  "upload",
  "analysis",
  "dashboard",
  "clients",
  "programs",
  "exercises",
] as const;
export const TOP_LEVEL_TAB_LABELS: Record<(typeof TOP_LEVEL_TABS)[number], string> = {
  home: "Home",
  train: "Train",
  track: "Track",
  nutrition: "Nutrition",
  upload: "Upload",
  analysis: "Analysis",
  dashboard: "Dashboard",
  clients: "Clients",
  programs: "Programs",
  exercises: "Exercises",
};


const topLevelTabSchema = z.enum(TOP_LEVEL_TABS);

const surfaceSchema = z.enum(["client", "coach", "admin", "public"]);

// --- ultrack RN telemetry expansion (docs/44 §6.1/§6.2) ------------------
// Enums below are mirrored VERBATIM from the RN client contract; any payload
// change re-opens docs/44 first (cross-repo drift risk — see docs/44 §11).
export const ONBOARDING_STEP_KEYS = [
  "feature-select",
  "units",
  "health-disclaimer",
  "training-predefined",
  "training-experience",
  "training-goal",
  "training-days",
  "training-time",
  "training-focus",
  "training-injuries",
  "training-rpe",
  "training-summary",
  "measurements-weight",
  "nutrition-track",
  "nutrition-calories",
  "nutrition-macros",
  "daily-wizard-setup",
  // Onboarding step keys the RN client emits that were not yet allowlisted here;
  // until allowlisted, /api/telemetry silently drops them per-item (HTTP 200,
  // droppedInvalidCount). `nutrition-macro-targets` was left un-added by a prior
  // batch; the remaining four are new RN onboarding steps.
  "nutrition-macro-targets",
  "health-permission",
  "import-offer",
  "log-rpe",
  "strength-1rm",
  // The GOAL steps. These were never allowlisted, so every one of them has been
  // dropped since the goals lane shipped — and the drop is invisible from the
  // client (HTTP 200 + droppedInvalidCount), which is why it survived several
  // batches unnoticed. Cross-referenced against the RN union
  // `OnboardingEventStepKey` (src/lib/analytics/onboarding-event-types.ts) AND
  // the wizard registry, so every key below is a REAL emitted step, not a
  // speculative addition. `goal-review` is deliberately absent: it is a dead
  // entry in the RN union that no registry step emits (being removed there).
  "measurements-weight-goal",
  "measurements-weight-goal-details",
  // docs/67 lane F split the weight goal into weights + pace — the pace step is
  // the newest of these.
  "measurements-weight-goal-pace",
  "measurements-body-goal",
  "measurements-body-goal-details",
  "training-strength-goal",
  "training-strength-goal-details",
  "daily-routine-opt-in",
] as const;

export const PAID_FEATURES = [
  "analysisTab",
  "unlimitedWorkouts",
  "fullAnalytics",
  "customMeasurements",
  "colorThemes",
  "goalSetting",
  "peakWeek",
  "voiceProgramCreator",
  "chartPanZoom",
  "programGenerator",
  "programPeriodization",
  "mealScan",
  "voiceNutrition",
] as const;

// ultrack #229 / ADR-0039 multi-dimensional PR vocabulary; keep in sync
// with the RN client's pr_type values.
export const PR_TYPES = [
  "weight",
  "reps_at_weight",
  "rpe_efficiency",
  "added_load",
  "reps_at_added_load",
  "least_assist",
  "reps_at_assist",
  "reps",
  "fastest_time_at_reps",
  "reps_at_time",
  "duration",
  "distance",
  "fastest_pace_at_distance",
  "distance_at_time",
  "steps",
  "fastest_time_at_steps",
  "steps_at_time",
  "rounds",
  "same_rounds_less_time",
] as const;

// --- import-event enums (docs/27 §5) — mirrored VERBATIM from the RN client
// contract (ultrack src/lib/analytics/wire-contract-schemas.ts). The RN client
// already emits these; until they were allowlisted here /api/telemetry silently
// dropped them (HTTP 200, droppedInvalidCount).
export const IMPORT_EVENT_SOURCES = [
  "fitbit",
  "withings",
  "libra",
  "generic_csv",
  "hevy",
  "strong",
  "liftoff",
  "lyfta",
  "cronometer",
  "myfitnesspal",
  "apple_health",
  "health_connect",
] as const;

export const IMPORT_EVENT_DOMAINS = ["weight", "body_comp", "training", "nutrition"] as const;

export const IMPORT_FAILURE_STAGES = ["pick", "parse", "preview", "commit", "undo"] as const;

const onboardingStepSchema = z.enum(ONBOARDING_STEP_KEYS);
// Deliberately narrower than surfaceSchema; mirrors the RN client contract
// (cross-repo parity, docs/44 §11 drift rule).
const onboardingSurfaceSchema = z.enum(["client", "coach"]);
const paidFeatureSchema = z.enum(PAID_FEATURES);
const paywallGateSchema = z.enum([
  "range",
  "theme",
  "program_day",
  "pan_zoom",
  "analysis_subfeature",
]);
const prTypeSchema = z.enum(PR_TYPES);
const entitlementSourceSchema = z.enum(["server", "revenuecat", "cache", "fallback"]);
const generationSourceSchema = z.enum(["llm", "fallback"]);
const voiceFailStageSchema = z.enum([
  "recording_failed",
  "transcribe_failed",
  "quota_exceeded",
  "empty_transcript",
  "persist_failed",
]);
const generationDurationBucketSchema = z.enum(["<2s", "2-5s", "5-15s", "15-30s", "30s+"]);
const sessionDurationBucketSchema = z.enum(["<1m", "1-5m", "5-15m", "15-30m", "30m+"]);
// RN docs/56 A7/A4: `activity_sleep` is the new Analysis-hub Activity & Sleep
// destination; `goals` is the typed-goals hub (nutrition/strength/measurement).
// Both are ratified analysis sections — added here so analysis_viewed.section
// from the RN client is accepted (else the event is silently dropped, HTTP 200).
// `pr_history` + `insights` (ultrack #474 lane 4b677406): PR-history and
// Training-insights tiles now emit analysis_viewed; same accept-or-drop rule.
const analysisSectionSchema = z.enum([
  "hub",
  "training",
  "peak_week",
  "measurements",
  "nutrition",
  "activity_sleep",
  "goals",
  "pr_history",
  "insights",
]);
// RN docs/56 A4 / ADR-0006 goal telemetry (schema-only allowlist; RN owns the
// emit sites in L8b). goal_type spans all four typed goal categories; `kind` is
// strength-only. Enums/counts/booleans/ids only — no free text (D1 rule).
const goalTypeSchema = z.enum(["nutrition", "strength", "measurement", "weight"]);
// `one_rm` is the current RN wire value. Keep legacy `top_load` accepted for
// backwards compatibility with older clients and historical payloads.
const goalKindSchema = z.enum(["weight_reps", "one_rm", "top_load"]);
const goalOutcomeSchema = z.enum(["completed", "cancelled"]);
const analysisRangeBucketSchema = z.union([
  z.literal(7),
  z.literal(30),
  z.literal(90),
  z.literal(365),
]);
const importEventSourceSchema = z.enum(IMPORT_EVENT_SOURCES);
const importEventDomainSchema = z.enum(IMPORT_EVENT_DOMAINS);
const importFailureStageSchema = z.enum(IMPORT_FAILURE_STAGES);
// ultrack #861 dashboard card log-tap enums — mirrored VERBATIM from
// the RN client contract (DashboardBodyCardId order in
// ultrack src/domain/client-home/dashboard-widget-preferences.ts).
// Lane G owns the RN emit side; the payload shape below is the shared lock.
export const DASHBOARD_BODY_CARD_IDS = [
  "verdict_headline",
  "training_overview",
  "training_stats",
  "daily_nutrition",
  "weekly_nutrition",
  "weight",
  "weight_stats",
  "measurements",
] as const;
// --------------------------------------------------------------------------
const nutritionMealTypeSchema = z.enum(NUTRITION_TELEMETRY_MEAL_TYPES);
const dashboardBodyCardSchema = z.enum(DASHBOARD_BODY_CARD_IDS);
const dashboardCardStateSchema = z.enum(["ghost", "ready"]);
const nutritionLogSourceSchema = z.enum(NUTRITION_LOG_SOURCES);
const weightLogSourceSchema = z.enum(WEIGHT_LOG_SOURCES);
const measurementsLogSourceSchema = z.enum(MEASUREMENTS_LOG_SOURCES);
const measurementsTabViewSourceSchema = z.enum(MEASUREMENTS_TAB_VIEW_SOURCES);
const photoUploadSourceSchema = z.enum(PHOTO_UPLOAD_SOURCES);
const comparisonCreatedSourceSchema = z.enum(COMPARISON_CREATED_SOURCES);
const videoUploadSourceSchema = z.enum(VIDEO_UPLOAD_SOURCES);

export const telemetryEventSchemas = {
  top_level_tab_opened: z.object({
    surface: surfaceSchema,
    tab: topLevelTabSchema,
  }),
  subtab_opened: z.object({
    surface: surfaceSchema,
    tab: topLevelTabSchema,
    subtab: z.string().min(1).max(64),
  }),
  screen_view: z.object({
    surface: surfaceSchema,
    path: z.string().min(1).max(256),
  }),
  navigation_timing: z.object({
    surface: surfaceSchema,
    from_path: z.string().min(1).max(256),
    to_path: z.string().min(1).max(256),
    intent_kind: z.string().min(1).max(64),
    severity: z.enum(["normal", "warn", "high", "critical"]),
    prefetch_hit: z.enum(["none", "route", "data", "route+data"]).default("none"),
    intent_to_dispatch_ms: z.number().int().nonnegative().max(120000).nullable().optional(),
    dispatch_to_route_change_ms: z.number().int().nonnegative().max(120000).nullable().optional(),
    route_change_to_paint_ms: z.number().int().nonnegative().max(120000).nullable().optional(),
    intent_to_paint_ms: z.number().int().nonnegative().max(120000).nullable().optional(),
    intent_to_data_ready_ms: z.number().int().nonnegative().max(120000).nullable().optional(),
    intent_to_visual_response_ms: z.number().int().nonnegative().max(120000).nullable().optional(),
  }),
  workout_started: z.object({
    surface: z.enum(["client", "coach"]),
    routine_id: z.string().uuid().optional(),
    client_program_id: z.string().uuid().optional(),
  }),
  workout_completed: z.object({
    surface: z.enum(["client", "coach"]),
    session_id: z.string().uuid(),
  }),
  // ultrack #477 — one abandon per dead session; reason splits the 61.2%
  // started-never-completed gap by cause. Full Zod object (keys ARE the ingest
  // allowlist); unknown names drop per-item with HTTP 200 in /api/telemetry.
  workout_abandoned: z.object({
    surface: z.enum(["client", "coach"]),
    session_id: z.string().uuid(),
    reason: z.enum(["explicit-cancel", "stale-purge", "discard-and-start"]),
  }),
  nutrition_log_added: z.object({
    surface: z.enum(["client", "coach"]),
    meal_type: nutritionMealTypeSchema.optional(),
    source: nutritionLogSourceSchema.optional(),
    count: z.number().int().positive().max(300).optional(),
  }),
  weight_log_added: z.object({
    surface: z.enum(["client", "coach"]),
    source: weightLogSourceSchema.optional(),
    unit: z.enum(["kg", "lbs"]).optional(),
  }),
  measurements_logged: z.object({
    surface: z.enum(["client", "coach"]),
    source: measurementsLogSourceSchema,
    measurement_count: z.number().int().positive().max(50),
  }),
  measurements_tab_viewed: z.object({
    surface: z.enum(["client", "coach"]),
    source: measurementsTabViewSourceSchema.optional(),
  }),
  photo_uploaded: z.object({
    surface: z.enum(["client", "coach"]),
    source: photoUploadSourceSchema,
  }),
  comparison_created: z.object({
    surface: z.enum(["client", "coach"]),
    source: comparisonCreatedSourceSchema,
    media_count: z.number().int().positive().max(50),
  }),
  video_uploaded: z.object({
    surface: z.enum(["client", "coach"]),
    source: videoUploadSourceSchema.optional(),
    success: z.boolean(),
  }),
  video_opened: z.object({
    surface: z.enum(["client", "coach"]),
  }),
  error_event: z.object({
    surface: surfaceSchema,
    area: z.string().min(1).max(64),
    code: z.string().min(1).max(64).optional(),
    // ultrack #871 — open bounded string mirroring `code` (NOT a closed enum:
    // the operation vocabulary is open-ended across ~20 client domains and an
    // enum would DROP whole events for any new operation). Optional so older
    // client versions emitting without it still validate.
    operation: z.string().min(1).max(64).optional(),
  }),
  // --- import events (docs/27 §5) — mirrored VERBATIM from the RN client
  // contract. Object keys ARE the ingest allowlist; the RN client emits these
  // already, so allowlisting stops the silent per-item drop in /api/telemetry.
  import_started: z.object({
    surface: z.enum(["client", "coach"]),
    source: importEventSourceSchema,
    domain: importEventDomainSchema,
  }),
  import_preview: z.object({
    surface: z.enum(["client", "coach"]),
    source: importEventSourceSchema,
    domain: importEventDomainSchema,
    rows: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
  }),
  import_completed: z.object({
    surface: z.enum(["client", "coach"]),
    source: importEventSourceSchema,
    domain: importEventDomainSchema,
    rows: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
  }),
  import_failed: z.object({
    surface: z.enum(["client", "coach"]),
    source: importEventSourceSchema,
    domain: importEventDomainSchema,
    stage: importFailureStageSchema,
  }),
  import_undone: z.object({
    surface: z.enum(["client", "coach"]),
    source: importEventSourceSchema,
    domain: importEventDomainSchema,
  }),
  // --- ultrack RN telemetry expansion (docs/44 §6.1) ----------------------
  // Object keys ARE the ingest allowlist; unknown names drop per-item with
  // HTTP 200 in /api/telemetry (droppedInvalidCount).
  // All five onboarding funnel events carry surface + REQUIRED ISO-8601 occurred_at
  // (issue #88 (a)). Duration = Date.parse(completed.occurred_at) -
  // Date.parse(started.occurred_at); step/fork timestamps are ORDERING EVIDENCE ONLY
  // and deliberately are not wired into duration.
  onboarding_step_viewed: z.object({
    surface: onboardingSurfaceSchema,
    step: onboardingStepSchema,
    occurred_at: z.string().datetime(),
  }),
  onboarding_step_completed: z.object({
    surface: onboardingSurfaceSchema,
    step: onboardingStepSchema,
    skipped: z.boolean(),
    occurred_at: z.string().datetime(),
  }),
  onboarding_program_fork: z.object({
    surface: onboardingSurfaceSchema,
    choice: z.enum(["predefined", "manual"]),
    occurred_at: z.string().datetime(),
  }),
  onboarding_completed: z.object({
    surface: onboardingSurfaceSchema,
    features: z.number().int().min(0).max(3),
    occurred_at: z.string().datetime(),
  }),
  onboarding_started: z.object({
    surface: onboardingSurfaceSchema,
    occurred_at: z.string().datetime(),
  }),
  voice_flow_started: z.object({
    surface: surfaceSchema,
  }),
  voice_transcribed: z.object({
    surface: surfaceSchema,
    usable: z.boolean(),
  }),
  voice_generated: z.object({
    surface: surfaceSchema,
    source: generationSourceSchema,
  }),
  voice_accepted: z.object({
    surface: surfaceSchema,
    days: z.number().int().nonnegative().max(300),
  }),
  voice_discarded: z.object({
    surface: surfaceSchema,
  }),
  voice_failed: z.object({
    surface: surfaceSchema,
    stage: voiceFailStageSchema,
  }),
  program_generated: z.object({
    surface: surfaceSchema,
    source: generationSourceSchema,
    duration_bucket: generationDurationBucketSchema,
  }),
  program_created: z.object({
    surface: surfaceSchema,
    mode: z.enum(["freeform", "periodized", "voice"]),
  }),
  manual_builder_completed: z.object({
    surface: surfaceSchema,
    steps: z.number().int().nonnegative().max(300),
  }),
  paywall_gate_hit: z.object({
    surface: surfaceSchema,
    feature: paidFeatureSchema,
    source: entitlementSourceSchema,
  }),
  paywall_reached: z.object({
    feature: paidFeatureSchema,
    wouldBlockOnFree: z.boolean(),
    surface: surfaceSchema,
    gate: paywallGateSchema,
  }),
  vault_viewed: z.object({
    surface: surfaceSchema,
    feature: paidFeatureSchema,
  }),
  // P3 purchase funnel (docs/36 §7-P3) — mirrored VERBATIM from the RN client
  // contract. feature is OPTIONAL on completed (a gate-driven buy carries it; a
  // restore/non-gated buy does not) and absent on restored; surface uses the
  // inline z.enum(["client","coach"]) exactly as the RN mirror declares them.
  purchase_completed: z.object({
    surface: z.enum(["client", "coach"]),
    feature: paidFeatureSchema.optional(),
  }),
  purchase_restored: z.object({
    surface: z.enum(["client", "coach"]),
  }),
  pr_achieved: z.object({
    surface: surfaceSchema,
    pr_type: prTypeSchema,
    count: z.number().int().nonnegative().max(300),
  }),
  analysis_viewed: z.object({
    surface: surfaceSchema,
    section: analysisSectionSchema,
  }),
  analysis_range_changed: z.object({
    surface: surfaceSchema,
    range_bucket: analysisRangeBucketSchema,
    locked: z.boolean(),
  }),
  // --- docs/56 A4 / ADR-0006 typed-goal funnel (schema-only; RN emits in L8b).
  // goal_id is a uuid (allowed id — not free text). progress_pct is an integer
  // 0..100. Completion rate = goal_ended(outcome=completed)/goal_started,
  // derived server-side; progress = (last-start)/(target-start) direction-
  // agnostic, clamped 0..100, at-most-once/day/goal (semantics owned by L8b).
  goal_started: z.object({
    surface: surfaceSchema,
    goal_type: goalTypeSchema,
    goal_id: z.string().uuid(),
    kind: goalKindSchema.optional(), // strength only
    has_deadline: z.boolean(),
  }),
  goal_progress_updated: z.object({
    surface: surfaceSchema,
    goal_type: goalTypeSchema,
    goal_id: z.string().uuid(),
    progress_pct: z.number().int().min(0).max(100),
  }),
  goal_ended: z.object({
    surface: surfaceSchema,
    goal_type: goalTypeSchema,
    goal_id: z.string().uuid(),
    outcome: goalOutcomeSchema,
    final_progress_pct: z.number().int().min(0).max(100),
  }),
  app_opened: z.object({
    surface: surfaceSchema,
  }),
  session_summary: z.object({
    surface: surfaceSchema,
    duration_bucket: sessionDurationBucketSchema,
  }),
  // --- docs/44 §6.2 — contract-only events (emitted by the docs/42/43 RN
  // screens; the names ship here first so those lanes only add track calls).
  pr_filter_changed: z.object({
    surface: surfaceSchema,
    pr_type: prTypeSchema,
  }),
  pr_chart_bar_tapped: z.object({
    surface: surfaceSchema,
  }),
  insights_section_viewed: z.object({
    surface: surfaceSchema,
    // Placeholder cap per docs/44 §6.2 — tighten to the closed enum once the
    // insights screen's plan fixes its section list.
    section: z.string().min(1).max(64),
  }),
  // --- ultrack #861 dashboard body-card log-tap (lane G owns the RN
  // emit side; object keys ARE the ingest allowlist). surface is the RN
  // FeatureSurface ('client' | 'coach'); card is the tapped body-card
  // id; state is whether the card was ghost or ready when tapped.
  // card strings are ids (allowed ids — not free text). Enums only (D1 rule).
  dashboard_card_log_tapped: z.object({
    surface: z.enum(["client", "coach"]),
    card: dashboardBodyCardSchema,
    state: dashboardCardStateSchema,
  }),
  // --- ultrack #1094 stuck-sync daily report (RN emits on launch when
  // anything is stuck, at most once per device per day). Object keys ARE the
  // ingest allowlist; unknown names drop per-item with HTTP 200 in
  // /api/telemetry. surface is the RN FeatureSurface ('client' | 'coach');
  // op/code/status strings are ids (allowed ids — not free text); counts
  // only, no content (D1 rule). by_op is client-sorted n desc, max 12.
  sync_stuck: z.object({
    surface: z.enum(["client", "coach"]),
    stuck_total: z.number().int().nonnegative(),
    direct_count: z.number().int().nonnegative(),
    waiting_count: z.number().int().nonnegative(),
    rearm_eligible_count: z.number().int().nonnegative(),
    oldest_stuck_hours: z.number().int().nonnegative(),
    by_op: z
      .array(
        z.object({
          op: z.string().min(1).max(64),
          code: z.string().min(1).max(64).nullable(),
          status: z.string().min(1).max(32),
          n: z.number().int().positive(),
        })
      )
      .max(12),
  }),
  // --- docs/096 founding-member RN-launch popup (web /client surface). Object
  // keys ARE the ingest allowlist; unknown names drop per-item with HTTP 200 in
  // /api/telemetry. `variant` is which popup copy was shown; opted_in is whether
  // the user asked to be emailed at RN launch. Enums/booleans only (D1 rule).
  rn_launch_notice_shown: z.object({
    variant: z.enum(["founder_full", "existing_light"]),
  }),
  rn_launch_optin_submitted: z.object({
    variant: z.enum(["founder_full", "existing_light"]),
    opted_in: z.boolean(),
  }),
  // -------------------------------------------------------------------------
} as const;

export type TelemetryEventName = keyof typeof telemetryEventSchemas;
export type TelemetryEventPayload<TEventName extends TelemetryEventName> = z.infer<
  (typeof telemetryEventSchemas)[TEventName]
>;

export function isTelemetryEventName(value: string): value is TelemetryEventName {
  return value in telemetryEventSchemas;
}

export function validateTelemetryEvent<TEventName extends TelemetryEventName>(
  eventName: TEventName,
  payload: unknown
): TelemetryEventPayload<TEventName> {
  return telemetryEventSchemas[eventName].parse(payload);
}
